import { useFormContext } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { Warning } from '@phosphor-icons/react'
import PhoneInput from 'react-phone-number-input/react-hook-form-input-core'
import type { MetadataJson } from 'libphonenumber-js'

import phoneMeta from '@/cart/utils/metadata.phone.json'

import type { TDates, TDeliveryDetail } from '@/main/app/schema'
import {
  fieldsetClassList,
  inputClassList,
  labelClassList,
  legendClassList,
} from '@/main/app/utils/classNames'

// import { DeliveryPlaceInput, AutocompletePredictionResult } from './DeliveryPlaceInput'

type Delivery = {
  shippingMethod: 'delivery'
  shippingDetail: Partial<TDeliveryDetail> & {
    deliveryType: 'roundtrip' | 'multitrip' | 'deliveryonly' | 'returnonly'
  }
}
type DatesAndDelivery = { dates: TDates; shipping: Delivery }

function DeliveryAddress({ legend, path }: { legend: string; path: 'delivery' | 'return' }) {
  const {
    register,
    control,
    watch,
    formState: { errors },
  } = useFormContext<DatesAndDelivery>()

  const deliveryType = watch('shipping.shippingDetail.deliveryType')
  const isDisabled = !deliveryType

  const group = 'shipping.shippingDetail'
  const groupErrors = errors?.shipping?.shippingDetail

  const contactKey: `${typeof path}Contact` = `${path}Contact`
  const phoneKey: `${typeof path}Phone` = `${path}Phone`
  const businessKey: `${typeof path}Business` = `${path}Business`
  const addressKey: `${typeof path}Address` = `${path}Address`
  const unitKey: `${typeof path}Unit` = `${path}Unit`
  const instructionsKey: `${typeof path}Instructions` = `${path}Instructions`
  const cityKey: `${typeof path}City` = `${path}City`
  const zipKey: `${typeof path}Zip` = `${path}Zip`

  const contact: `${typeof group}.${typeof contactKey}` = `${group}.${contactKey}`
  const phone: `${typeof group}.${typeof phoneKey}` = `${group}.${phoneKey}`
  const business: `${typeof group}.${typeof businessKey}` = `${group}.${businessKey}`
  const address: `${typeof group}.${typeof addressKey}` = `${group}.${addressKey}`
  const unit: `${typeof group}.${typeof unitKey}` = `${group}.${unitKey}`
  const instructions: `${typeof group}.${typeof instructionsKey}` = `${group}.${instructionsKey}`
  const city: `${typeof group}.${typeof cityKey}` = `${group}.${cityKey}`
  const zip: `${typeof group}.${typeof zipKey}` = `${group}.${zipKey}`

  return (
    <fieldset
      role="group"
      disabled={isDisabled}
      className={`${fieldsetClassList} twc-bg-white dark:twc-bg-slate-800/40 disabled:twc-opacity-40 prose-p:twc-my-0.5`}
    >
      <legend className={legendClassList}>{legend} Info</legend>
      <div className="twc-flex twc-flex-col sm:twc-flex-row sm:twc-gap-4">
        <div className="twc-grow">
          <label className={labelClassList} htmlFor={contact}>
            On-Site Contact Name
          </label>
          <input
            className={`${inputClassList} twc-mt-1`}
            type="text"
            id={contact}
            autoComplete="name"
            aria-invalid={groupErrors && groupErrors ? 'true' : 'false'}
            aria-describedby={`${contact}-feedback`}
            {...register(contact, { required: true })}
          />
          <div
            id={`${contact}-feedback`}
            aria-live="polite"
            className="twc-min-h-[1.25rem] twc-prose-sm twc-text-red-500"
          >
            <ErrorMessage
              className="twc-prose-sm twc-text-red-500"
              errors={errors}
              name={contact}
              as="p"
            />
          </div>
        </div>

        <div className="twc-grow">
          <label className={labelClassList} htmlFor={phone}>
            On-Site Contact Phone Number
          </label>
          {/* <input
            className={`${inputClassList} twc-mt-1`}
            type="tel"
            id={phone}
            autoComplete="tel"
            aria-invalid={groupErrors && groupErrors[phoneKey] ? 'true' : 'false'}
            aria-describedby={`${phone}-feedback`}
            {...register(phone, { required: true })}
          /> */}

          <PhoneInput
            control={control}
            name={phone}
            rules={{ required: true }}
            className={`${inputClassList} twc-mt-1`}
            country="US"
            id={phone}
            aria-invalid={groupErrors && groupErrors[phoneKey] ? 'true' : 'false'}
            aria-describedby={`${phone}-feedback`}
            metadata={phoneMeta as MetadataJson}
          />

          {/* <Controller
            name={phone}
            rules={{
              required: true,
            }}
            render={({ field }) => (
              <PhoneInput
                className={`${inputClassList} twc-mt-1`}
                country="US"
                id={phone}
                aria-invalid={groupErrors && groupErrors[phoneKey] ? 'true' : 'false'}
                aria-describedby={`${phone}-feedback`}
                {...field}
              />
            )}
          /> */}

          <div
            id={`${phone}-feedback`}
            aria-live="polite"
            className="twc-min-h-[1.25rem] twc-prose-sm twc-text-red-500"
          >
            <ErrorMessage
              className="twc-prose-sm twc-text-red-500"
              errors={errors}
              name={phone}
              as="p"
            />
          </div>
        </div>
      </div>

      {/* <div className="twc-not-prose twc-mt-4"> */}
      <div>
        {/* <DeliveryPlaceInput onSelect={handleSelectAddress} /> */}
        <label className={labelClassList} htmlFor={business}>
          Business/Studio/Company Name
        </label>

        <input
          className={`${inputClassList} twc-mt-1`}
          type="text"
          id={business}
          autoComplete="organization"
          aria-invalid={groupErrors && groupErrors[businessKey] ? 'true' : 'false'}
          aria-describedby={`${business}-feedback`}
          {...register(business, { required: true })}
        />

        <div
          id={`${business}-feedback`}
          aria-live="polite"
          className="twc-min-h-[1.25rem] twc-prose-sm twc-text-red-500"
        >
          <ErrorMessage
            className="twc-mt-1 twc-mb-0 twc-prose-sm twc-text-red-500"
            errors={errors}
            name={business}
            as="p"
          />
        </div>
      </div>

      <div className="twc-flex twc-flex-wrap sm:twc-gap-4">
        <div className="twc-grow-[999] sm:twc-basis-0">
          <label className={labelClassList} htmlFor={address}>
            Street
          </label>
          <input
            className={inputClassList}
            type="text"
            id={address}
            autoComplete="address-line1"
            aria-invalid={groupErrors && groupErrors[addressKey] ? 'true' : 'false'}
            aria-describedby={`${address}-feedback`}
            {...register(address, { required: true })}
          />

          <div
            id={`${address}-feedback`}
            aria-live="polite"
            className="twc-min-h-[1.25rem] twc-prose-sm twc-text-red-500"
          >
            <ErrorMessage
              className="twc-prose-sm twc-text-red-500"
              errors={errors}
              name={address}
              as="p"
            />
          </div>
        </div>
        <div className="sm:twc-grow-0 sm:twc-basis-48">
          <label className={labelClassList} htmlFor={unit}>
            Suite/Unit/Floor
          </label>
          <input
            className={inputClassList}
            type="text"
            id={unit}
            autoComplete="address-line2"
            aria-invalid={groupErrors && groupErrors[unitKey] ? 'true' : 'false'}
            aria-describedby={`${unit}-feedback`}
            {...register(unit)}
          />

          <div
            id={`${unit}-feedback`}
            aria-live="polite"
            className="twc-min-h-[1.25rem] twc-prose-sm twc-text-red-500"
          >
            <ErrorMessage
              className="twc-prose-sm twc-text-red-500"
              errors={errors}
              name={unit}
              as="p"
            />
          </div>
        </div>
      </div>

      <div className="twc-flex twc-flex-wrap sm:twc-gap-4">
        <div className="twc-grow-[999] sm:twc-basis-0">
          <label className={labelClassList} htmlFor={city}>
            NYC Borough
          </label>
          <input
            className={inputClassList}
            type="text"
            list={`${city}-list`}
            id={city}
            autoComplete="address-level2"
            aria-invalid={groupErrors && groupErrors[cityKey] ? 'true' : 'false'}
            aria-describedby={`${city}-feedback`}
            {...register(city, { required: true })}
          />

          <datalist id={`${city}-list`}>
            <option>Manhattan</option>
            <option>Brooklyn</option>
            <option>Queens</option>
            <option>Bronx</option>
            <option>Staten Island</option>
          </datalist>

          <div
            id={`${city}-feedback`}
            aria-live="polite"
            className="twc-min-h-[1.25rem] twc-prose-sm twc-text-red-500"
          >
            <ErrorMessage
              className="twc-prose-sm twc-text-red-500"
              errors={errors}
              name={city}
              as="p"
            />
          </div>
        </div>
        <div className="sm:twc-grow-0 sm:twc-basis-48">
          <label className={labelClassList} htmlFor={zip}>
            NYC ZIP Code
          </label>
          <input
            className={inputClassList}
            type="text"
            id={zip}
            autoComplete="postal-code"
            aria-invalid={groupErrors && groupErrors[zipKey] ? 'true' : 'false'}
            aria-describedby={`${zip}-feedback`}
            {...register(zip, { required: true })}
          />

          <div
            id={`${zip}-feedback`}
            aria-live="polite"
            className="twc-min-h-[1.25rem] twc-prose-sm twc-text-red-500"
          >
            <ErrorMessage
              className="twc-prose-sm twc-text-red-500"
              errors={errors}
              name={zip}
              as="p"
            />
          </div>
        </div>
      </div>

      <div className="twc-mt-4 twc-not-prose">
        <label className={labelClassList} htmlFor={instructions}>
          Loading Dock/Delivery Instructions/Requirements
        </label>

        <textarea
          id={instructions}
          className="aria-[invalid=true]:twc-border-red-600 twc-form-textarea twc-rounded dark:twc-bg-slate-800 dark:twc-border-slate-600 dark:twc-text-zinc-200 twc-w-full min-h-[6rem]"
          aria-invalid={groupErrors && groupErrors[instructionsKey] ? 'true' : 'false'}
          aria-describedby={`${instructions}-desc`}
          {...register(instructions)}
        />

        <p id={`${instructions}-desc`} className="twc-prose-sm twc-mb-2">
          e.g. COI requirements, flights of stairs, time restrictions, and/or parking restrictions
        </p>

        <ErrorMessage
          className="twc-prose-sm twc-text-red-500"
          errors={errors}
          name={instructions}
          as="p"
        />
      </div>
    </fieldset>
  )
}

export function DeliveryFields() {
  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext<DatesAndDelivery>()

  const deliveryType = watch('shipping.shippingDetail.deliveryType')
  const isDisabled = !deliveryType

  return (
    <div className="twc-bg-white/75 dark:twc-bg-slate-900/50 twc-p-4 sm:twc-pt-8 sm:twc-px-6 sm:twc-pb-4 twc-rounded twc-shadow-lg twc-mb-12 twc-flex twc-flex-col twc-gap-8">
      <div className="twc-bg-amber-200/50 dark:twc-bg-violet-400/25 twc-p-4 twc-rounded">
        <h4 className="twc-font-semibold twc-mt-0 twc-mb-1">About deliveries with ACME</h4>

        <ul className="twc-list-disc prose-li:marker:twc-text-stone-900 dark:prose-li:marker:twc-text-stone-300 pl-4 twc-prose-sm twc-mb-0">
          <li className="twc-mb-1 pl-1">
            Deliveries and returns can be scheduled <strong>Monday thru Friday</strong>
          </li>
          <li className="twc-mb-1 pl-1">
            ACME can arrange a 3rd party courier service for an additional fee
          </li>
          <li className="twc-mb-1 pl-1">
            Service available in{' '}
            <strong>Manhattan, Brooklyn, Queens, the Bronx, and Staten Island</strong>
          </li>
          <li className="twc-mb-1 pl-1">
            <strong>
              Complete address and on-site contact details are required to provide a quote
            </strong>
          </li>
        </ul>
      </div>

      <fieldset
        role="radiogroup"
        className={`${fieldsetClassList} twc-bg-white dark:twc-bg-slate-800/40 prose-p:twc-my-0.5`}
      >
        <legend className={legendClassList}>Delivery Type</legend>

        <div className="twc-flex twc-flex-col twc-gap-4">
          <label className={`${labelClassList} twc-inline-flex twc-items-center twc-gap-2`}>
            <input
              className="twc-form-radio twc-peer"
              type="radio"
              value="roundtrip"
              {...register('shipping.shippingDetail.deliveryType')}
            />{' '}
            <span className="twc-text-base peer-checked:twc-font-bold peer-checked:twc-text-blue-800 dark:peer-checked:twc-text-blue-300">
              Round trip <small>(deliver and return to/from the same location)</small>
            </span>
          </label>
          <label className={`${labelClassList} twc-inline-flex twc-items-center twc-gap-2`}>
            <input
              className="twc-form-radio twc-peer"
              type="radio"
              value="multitrip"
              {...register('shipping.shippingDetail.deliveryType')}
            />{' '}
            <span className="twc-text-base peer-checked:twc-font-bold peer-checked:twc-text-blue-800 dark:peer-checked:twc-text-blue-300">
              Multiple locations <small>(deliver and return to/from two separate locations)</small>
            </span>
          </label>
          <label className={`${labelClassList} twc-inline-flex twc-items-center twc-gap-2`}>
            <input
              className="twc-form-radio twc-peer"
              type="radio"
              value="deliveryonly"
              {...register('shipping.shippingDetail.deliveryType')}
            />{' '}
            <span className="twc-text-base peer-checked:twc-font-bold peer-checked:twc-text-blue-800 dark:peer-checked:twc-text-blue-300">
              Delivery only <small>(I will return to ACME myself)</small>
            </span>
          </label>
          <label className={`${labelClassList} twc-inline-flex twc-items-center twc-gap-2`}>
            <input
              className="twc-form-radio twc-peer"
              type="radio"
              value="returnonly"
              {...register('shipping.shippingDetail.deliveryType')}
            />{' '}
            <span className="twc-text-base peer-checked:twc-font-bold peer-checked:twc-text-blue-800 dark:peer-checked:twc-text-blue-300">
              Return only <small>(I will pick-up from ACME myself)</small>
            </span>
          </label>
        </div>

        <div id="shipping.shippingDetail.deliveryType-feedback" aria-live="polite">
          <ErrorMessage
            className="twc-prose-sm twc-text-red-500"
            errors={errors}
            name="shipping.shippingDetail.deliveryType"
            as="p"
          />
        </div>
      </fieldset>

      {isDisabled && (
        <p className="twc-mb-2 twc-bg-amber-200/50 twc-text-stone-700 dark:twc-bg-amber-500/50 dark:twc-text-stone-100 self-start twc-prose-sm twc-p-4 twc-rounded twc-flex twc-items-center twc-gap-1 twc-w-full twc-text-center twc-justify-center">
          <Warning size={14} /> Choose a &quot;Delivery Type&quot; to continue
        </p>
      )}

      <fieldset
        disabled={isDisabled}
        className={`${fieldsetClassList} twc-bg-white dark:twc-bg-slate-800/40 disabled:twc-opacity-40`}
      >
        <legend className={legendClassList}>Time Windows</legend>

        <div className="twc-flex twc-flex-col sm:twc-flex-row twc-flex-wrap twc-justify-between prose-p:twc-my-0.5">
          {deliveryType !== 'returnonly' && (
            <div className="twc-basis-[48%]">
              <label className={labelClassList} htmlFor="shipping.shippingDetail.startTime">
                Delivery Time
              </label>
              <br />
              <select
                id="shipping.shippingDetail.startTime"
                {...register('shipping.shippingDetail.startTime')}
                defaultValue=""
                className="twc-form-select twc-rounded dark:twc-bg-slate-700 twc-mt-0.5 twc-w-full"
              >
                <option value="" disabled={true}>
                  (Select delivery time window)
                </option>
                {/* <option>Anytime (10 AM - 4 PM)</option> */}
                <option>Morning (10 AM - 12 PM)</option>
                <option>Afternoon (12 PM - 2 PM)</option>
                <option>End of day (2 PM - 4 PM)</option>
              </select>

              <div
                id="shipping.shippingDetail.startTime-feedback"
                aria-live="polite"
                className="twc-min-h-[1.25rem] twc-prose-sm twc-text-red-500"
              >
                <ErrorMessage
                  className="twc-prose-sm twc-text-red-500"
                  errors={errors}
                  name="shipping.shippingDetail.startTime"
                  as="p"
                />
              </div>
            </div>
          )}

          {deliveryType !== 'deliveryonly' && (
            <div className="twc-basis-[48%]">
              <label className={labelClassList} htmlFor="shipping.shippingDetail.endTime">
                Return Pick-Up Time
              </label>
              <br />
              <select
                id="shipping.shippingDetail.endTime"
                {...register('shipping.shippingDetail.endTime')}
                defaultValue=""
                className="twc-form-select twc-rounded dark:twc-bg-slate-700 twc-mt-0.5 twc-w-full"
              >
                <option value="" disabled={true}>
                  (Select return time window)
                </option>
                {/* <option>Anytime (9 AM - 4 PM)</option> */}
                <option>Morning (9 AM - 12 PM)</option>
                <option>Afternoon (12 PM - 2 PM)</option>
                <option>End of day (2 PM - 4 PM)</option>
              </select>

              <div
                id="shipping.shippingDetail.endTime-feedback"
                aria-live="polite"
                className="twc-min-h-[1.25rem] twc-prose-sm twc-text-red-500"
              >
                <ErrorMessage
                  className="twc-prose-sm twc-text-red-500"
                  errors={errors}
                  name="shipping.shippingDetail.endTime"
                  as="p"
                />
              </div>
            </div>
          )}
        </div>
      </fieldset>

      {deliveryType !== 'returnonly' && <DeliveryAddress legend="Delivery" path="delivery" />}
      {(deliveryType === 'returnonly' || deliveryType === 'multitrip') && (
        <DeliveryAddress legend="Return" path="return" />
      )}
    </div>
  )
}
